/**
 * Manage dashboard admin accounts — AdminLTE cards & table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getDashboardAuthHeaders } from '../lib/dashboardSession';
import { getApiBase } from '../lib/apiBase';

export default function DashboardAdminPanel() {
  const [admins, setAdmins] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchAdmins = useCallback(async () => {
    setListLoading(true);
    setError('');
    try {
      const res = await fetch(`${getApiBase()}/api/dashboard/admins`, {
        headers: getDashboardAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Error ${res.status}`);
        return;
      }
      setAdmins(Array.isArray(data.admins) ? data.admins : []);
    } catch (err) {
      setError(err?.message || 'Failed to load admins');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const username = newUsername.trim();
    if (!username || !newPassword) {
      setError('Enter username and password');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/api/dashboard/admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getDashboardAuthHeaders(),
        },
        body: JSON.stringify({ username, password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Error ${res.status}`);
        return;
      }
      const label = data.admin?.username || username;
      setSuccess(
        data.admin?.updated
          ? `Password updated for "${label}" in the database.`
          : `Admin "${label}" saved to the database. They can sign in now.`
      );
      setNewUsername('');
      setNewPassword('');
      setConfirmPassword('');
      await fetchAdmins();
    } catch (err) {
      setError(err?.message || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-lg-5">
        <div className="card card-primary card-outline">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-user-plus mr-1" />
              Add dashboard admin
            </h3>
          </div>
          <div className="card-body">
            <p className="text-muted text-sm">
              Username and password are stored in the database (password is encrypted). Admins sign in on this page.
            </p>
            <form onSubmit={handleAddAdmin}>
              <div className="form-group">
                <label>Username or email</label>
                <input
                  type="text"
                  className="form-control"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="e.g. robinson@actiontourguide.com"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label>Confirm password</label>
                <input
                  type="password"
                  className="form-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-1" />
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-1" />
                    Save to database
                  </>
                )}
              </button>
            </form>
            {error && <p className="text-danger text-sm mt-3 mb-0">{error}</p>}
            {success && <p className="text-success text-sm mt-3 mb-0">{success}</p>}
          </div>
        </div>
      </div>

      <div className="col-lg-7">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-users mr-1" />
              Admins in database
            </h3>
            <div className="card-tools">
              <button
                type="button"
                className="btn btn-tool btn-sm"
                onClick={fetchAdmins}
                disabled={listLoading}
              >
                <i className={`fas fa-sync-alt ${listLoading ? 'fa-spin' : ''}`} />
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            {listLoading && admins.length === 0 ? (
              <p className="text-center text-muted py-4 mb-0">Loading…</p>
            ) : admins.length === 0 ? (
              <p className="text-center text-muted py-4 mb-0">
                No admins yet. Add one above or run: node scripts/create-dashboard-admin.js username password
              </p>
            ) : (
              <table className="table table-striped mb-0">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((a) => (
                    <tr key={a.id}>
                      <td>{a.username}</td>
                      <td>
                        {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
